import { useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

type Mode = "signin" | "signup";

/**
 * First-party email/password auth (Manus exit).
 * Residents can register; admin still signs in with ADMIN_* credentials.
 */
export default function Login() {
  const [, navigate] = useLocation();
  const params = new URLSearchParams(window.location.search);
  const returnPath = params.get("returnPath") || params.get("next") || "/";

  const [mode, setMode] = useState<Mode>("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const utils = trpc.useUtils();

  const goAfterAuth = async () => {
    await utils.auth.me.invalidate();
    const safe =
      returnPath.startsWith("/") && !returnPath.startsWith("//")
        ? returnPath
        : "/";
    navigate(safe);
  };

  const login = trpc.auth.login.useMutation({
    onSuccess: goAfterAuth,
  });
  const register = trpc.auth.register.useMutation({
    onSuccess: goAfterAuth,
  });

  const pending = login.isPending || register.isPending;
  const error = login.error || register.error;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pending) return;
    if (mode === "signup") {
      register.mutate({ name, email, password });
    } else {
      login.mutate({ email, password });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{mode === "signup" ? "Create account" : "Sign in"}</CardTitle>
          <CardDescription>
            Purva Swanlake PSAOA — residents and admins
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Button
              type="button"
              variant={mode === "signin" ? "default" : "outline"}
              className="flex-1"
              onClick={() => setMode("signin")}
            >
              Sign in
            </Button>
            <Button
              type="button"
              variant={mode === "signup" ? "default" : "outline"}
              className="flex-1"
              onClick={() => setMode("signup")}
            >
              Sign up
            </Button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "signup" && (
              <div className="space-y-2">
                <Label htmlFor="login-name">Full name</Label>
                <Input
                  id="login-name"
                  type="text"
                  autoComplete="name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">Password</Label>
              <Input
                id="login-password"
                type="password"
                autoComplete={
                  mode === "signup" ? "new-password" : "current-password"
                }
                required
                minLength={mode === "signup" ? 8 : 1}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {mode === "signup" && (
                <p className="text-xs text-slate-500">At least 8 characters</p>
              )}
            </div>
            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error.message}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={pending}>
              {pending
                ? mode === "signup"
                  ? "Creating account…"
                  : "Signing in…"
                : mode === "signup"
                  ? "Create account"
                  : "Sign in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
